import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, getDocs, doc, getDoc, setDoc, updateDoc, deleteField, serverTimestamp } from "firebase/firestore";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";


const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
const storage = getStorage(app);

export const fetchPermissibleSites = async (userEmail) => {
    console.log(`Fetching permissible sites for user: ${userEmail}`);

    const sitePermissionsRef = collection(db, "sitePermissions");
    const sitesRef = collection(db, "sites");

    try {
        const sitePermissionsSnapshot = await getDocs(sitePermissionsRef);
        const permissibleSiteIds = [];

        sitePermissionsSnapshot.forEach((doc) => {
            const data = doc.data();
            if (data && data.users && Array.isArray(data.users) && data.users.includes(userEmail)) {
                permissibleSiteIds.push(doc.id);
            }
        });

        console.log("Permissible site IDs:", permissibleSiteIds);

        const siteDetails = [];
        const sitesSnapshot = await getDocs(sitesRef);

        sitesSnapshot.forEach((doc) => {
            if (permissibleSiteIds.includes(doc.id)) {
                const siteData = doc.data();
                siteDetails.push({
                    name: doc.id,
                    thumbnailUrl: siteData.thumbnailUrl || "",
                });
            }
        });

        console.log("Fetched site details with thumbnails:", siteDetails);
        return siteDetails;
    } catch (error) {
        console.error("Error fetching permissible sites:", error);
        throw error;
    }
};

export const fetchSiteData = async (siteName) => {
    console.log(`Fetching data for site: ${siteName}`);
    try {
        const siteDocRef = doc(db, "sites", siteName);
        const siteDoc = await getDoc(siteDocRef);

        if (!siteDoc.exists()) {
            console.error(`Site "${siteName}" does not exist.`);
            throw new Error(`Site "${siteName}" not found.`);
        }

        const siteData = siteDoc.data();
        console.log(`Fetched data for site "${siteName}":`, siteData);

        return siteData;
    } catch (error) {
        console.error("Error fetching site data:", error);
        throw error;
    }
};

export const fetchStorageFile = async (filePath) => {
    try {
        const fileRef = ref(storage, filePath);
        const url = await getDownloadURL(fileRef);
        console.log(`Fetched file URL for ${filePath}:`, url);
        return url;
    } catch (error) {
        console.error(`Error fetching file ${filePath}:`, error);
        return null;
    }
};

export const fetchAllSiteData = async (userEmail) => {
    console.log(`Fetching all site data for user: ${userEmail}`);
    try {
        const permissibleSites = await fetchPermissibleSites(userEmail);
        const allSiteData = {};

        for (const site of permissibleSites) {
            const siteData = await fetchSiteData(site.name);
            allSiteData[site.name] = siteData;
        }

        console.log("Fetched all site data:", allSiteData);
        return allSiteData;
    } catch (error) {
        console.error("Error fetching all site data:", error);
        throw error;
    }
};

const fetchFileContent = async (fileUrl) => {
    try {
        console.log(`Fetching file content from URL: ${fileUrl}`);
        const response = await fetch(fileUrl);
        if (!response.ok) {
            throw new Error(`Failed to fetch file content from ${fileUrl}`);
        }
        const blob = await response.blob();
        console.log(`Fetched file content as blob for ${fileUrl}`);
        return blob;
    } catch (error) {
        console.error(`Error fetching file content for ${fileUrl}:`, error);
        return null;
    }
};

export const fetchOverlayData = async (siteName) => {
    try {
        console.log(`Fetching overlay data for site: ${siteName}`);
        const siteDocRef = doc(db, "sites", siteName);
        const siteDoc = await getDoc(siteDocRef);

        if (siteDoc.exists()) {
            const data = siteDoc.data();
            console.log("Raw overlay data from Firestore:", data.overlays);

            if (data.overlays) {
                const overlays = {};
                for (const [category, overlay] of Object.entries(data.overlays)) {
                    console.log(`Processing overlay category: ${category}`);
                    console.log("Overlay Firestore paths:", overlay);

                    const imageUrl = await fetchStorageFile(overlay.image);
                    const boundsUrl = await fetchStorageFile(overlay.bounds);

                    console.log(`Fetched URLs: image=${imageUrl}, bounds=${boundsUrl}`);

                    if (imageUrl && boundsUrl) {
                        const imageBlob = await fetchFileContent(imageUrl);
                        const boundsBlob = await fetchFileContent(boundsUrl);

                        console.log(`Fetched blobs: imageBlob=${imageBlob}, boundsBlob=${boundsBlob}`);

                        if (imageBlob && boundsBlob) {
                            overlays[category] = { image: imageBlob, bounds: boundsBlob };
                            console.log(`Overlay data fetched for category: ${category}`);
                        } else {
                            console.warn(`Failed to fetch blobs for category: ${category}`);
                        }
                    } else {
                        console.warn(`Missing image or bounds URLs for category: ${category}`);
                    }
                }

                console.log("Final overlays object:", overlays);
                return overlays;
            } else {
                console.warn(`No overlays found for site: ${siteName}`);
                return null;
            }
        } else {
            console.error(`Site ${siteName} does not exist in Firestore.`);
            return null;
        }
    } catch (error) {
        console.error("Error fetching overlay data:", error);
        throw error;
    }
};


export const fetchGeoJsonData = async (siteName) => {
    console.log(`Fetching GeoJSONs for site: ${siteName}`);
    const siteDocRef = doc(db, "sites", siteName);
    const siteDoc = await getDoc(siteDocRef);

    if (!siteDoc.exists()) {
        console.error(`Site "${siteName}" does not exist.`);
        return null;
    }

    const data = siteDoc.data();
    if (!data.polygons || !data.polygons.IB) {
        console.warn(`No IB polygons found for site: ${siteName}`);
        return null;
    }

    const fetchGeoJsonPromises = Object.entries(data.polygons.IB).map(async ([ib, url]) => {
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error(`Failed to fetch GeoJSON for IB ${ib}`);
            const json = await response.json();
            return { [ib]: json };
        } catch (error) {
            console.error(`Error fetching GeoJSON for IB ${ib}:`, error);
            return null;
        }
    });

    const results = await Promise.all(fetchGeoJsonPromises);
    return Object.assign({}, ...results.filter(Boolean));
};

export const fetchFaultData = async (siteName, date) => {
    try {
        const faultsCollection = collection(db, `sites/${siteName}/dates/${date}/faults`);
        const faultDocs = await getDocs(faultsCollection);
        const faults = {};

        faultDocs.forEach((doc) => {
            const data = doc.data();
            faults[doc.id] = {
                ID: data.ID || doc.id,
                category: data.category,
                latitude: data.latitude,
                longitude: data.longitude,
                level: data.level,
                location: data.location || "Unknown",
                images: data.images || []
            };
        });

        console.log("Fault data fetched:", faults);
        return faults;
    } catch (error) {
        console.error("Error fetching faults:", error);
        return null;
    }
};

export const fetchLatestDate = async (siteName) => {
    try {
        const datesCollectionRef = collection(db, `sites/${siteName}/dates`);
        const dateDocs = await getDocs(datesCollectionRef);
        const dateList = dateDocs.docs.map(doc => doc.id);

        if (dateList.length === 0) {
            console.warn("No dates found for site:", siteName);
            return null;
        }

        // Sort dates in descending order (newest first)
        dateList.sort((a, b) => b.localeCompare(a));

        console.log("Latest available date:", dateList[0]);
        return dateList[0]; // Pick the most recent date
    } catch (error) {
        console.error("Error fetching latest date:", error);
        return null;
    }
};

export const fetchBlockOutlineData = async (siteName) => {
    try {
        console.log(`Fetching Block Outline GeoJSON for site: ${siteName}`);

        const filePath = `siteFiles/${siteName}/mapPolygons/BlockOutline.geojson`;

        const fileUrl = await fetchStorageFile(filePath);
        console.log("Block outline file URL: ", fileUrl);

        if (!fileUrl) {
            console.error(`Failed to retrieve BlockOutline.geojson for site: ${siteName}`);
            return null;
        }

        const response = await fetch(fileUrl);
        if (!response.ok) {
            throw new Error(`Failed to fetch GeoJSON from URL: ${fileUrl}`);
        }

        const geoJson = await response.json();
        console.log(`Fetched BlockOutline.geojson successfully for site: ${siteName}`, geoJson);
        return geoJson;
    } catch (error) {
        console.error("Error fetching Block Outline GeoJSON:", error);
        return null;
    }
};

export const uploadFaultImage = async (siteName, date, faultLocation, imageUrl) => {
    const faultRef = doc(db, `sites/${siteName}/dates/${date}/faults/${faultLocation}`);
    const faultSnap = await getDoc(faultRef);

    if (faultSnap.exists()) {
        const faultData = faultSnap.data();
        const existingImages = faultData.images || [];

        await updateDoc(faultRef, {
            images: [...existingImages, { imageUrl, timestamp: serverTimestamp() }]
        });

        console.log("Image uploaded successfully with timestamp!");
    }
};


export const addPanelToRepairQueue = async (siteName, repairDateId, stage, panelData) => {
    try {
        const repairDateRef = doc(db, `sites/${siteName}/repairQueue/${repairDateId}`);
        const repairDateSnap = await getDoc(repairDateRef);

        if (repairDateSnap.exists()) {
            // Update the existing map with the new panel
            await updateDoc(repairDateRef, {
                [`${stage}.${panelData.panelId}`]: {
                    ...panelData,
                    timestamp: serverTimestamp()
                }
            });
        } else {
            // Create a new document with the initial map
            await setDoc(repairDateRef, {
                [stage]: {
                    [panelData.panelId]: {
                        ...panelData,
                        timestamp: serverTimestamp()
                    }
                }
            });
        }

        console.log(`Panel ${panelData.panelId} added to ${stage} for ${repairDateId}.`);
    } catch (error) {
        console.error("Error adding panel to repair queue:", error);
        throw error;
    }
};

export const fetchRepairDates = async (siteName) => {
    try {
        const queueRef = collection(db, `sites/${siteName}/repairQueue`);
        const snapshot = await getDocs(queueRef);
        return snapshot.docs.map(doc => doc.id);
    } catch (error) {
        console.error("Error fetching repair dates:", error);
        return [];
    }
};

export const fetchPanelsForRepairDate = async (siteName, repairDateId) => {
    try {
        const repairDateRef = doc(db, `sites/${siteName}/repairQueue/${repairDateId}`);
        const repairDateSnap = await getDoc(repairDateRef);

        if (!repairDateSnap.exists()) {
            console.warn(`No repair data found for date ${repairDateId}.`);
            return [];
        }

        const data = repairDateSnap.data();
        console.log("Repair queue data:", data);

        // Find the first key that has panel objects
        for (const [stage, panelMap] of Object.entries(data)) {
            if (typeof panelMap === "object" && panelMap !== null) {
                const panels = Object.values(panelMap);
                console.log(`Panels found under stage "${stage}":`, panels);
                return panels;
            }
        }

        console.warn("No panels found in any stage.");
        return [];
    } catch (error) {
        console.error("Error fetching panels for repair date:", error);
        return [];
    }
};

export async function deletePanelFromRepairQueue(siteName, repairDate, panelEntry) {
    const db = getFirestore();
    const docRef = doc(db, "sites", siteName, "repairQueue", repairDate);

    const panelId = panelEntry.panelId || panelEntry.location; // fallback just in case

    await updateDoc(docRef, {
        [`inspection.${panelId}`]: deleteField()
    });
}